import { getCookie } from "./utils/cookies";
import { waitForEl } from "./utils/waitForEl";
import { copyElementTextToClipboard } from "./utils/copyToClipboard";

/**
 * Triggers a dialog modal based on URL parameters and/or cookies, with dynamic title and content.
 * @param {Object} options - Configuration options.
 * @param {string} [options.param] - First URL parameter to check.
 * @param {string} [options.discountCode] - Second URL parameter to check (for content).
 * @param {string} [options.cookie] - Cookie name to check.
 * @param {string} [options.cookieContains] - Optional substring to check within the cookie value.
 * @param {'AND'|'OR'} [options.logicOperator] - Whether to require both param and cookie (AND) or either (OR). Default is 'OR'.
 * @param {string} options.dialogSelector - Selector for the dialog modal.
 * @param {string} options.campaignTitleTemplate - Template for the modal title. Use `${param}` for dynamic value.
 * @param {string} options.codeTemplate - Template for the modal content. Use `${discountCode}` for dynamic value.
 * @param {string} [options.sourceParam] - URL parameter to validate source (e.g., 'utm_source').
 * @param {string} [options.sourceValidation] - Expected value for the source parameter.
 * @param {string} [options.contentOverride] - Optional content override for the modal.
 */
function campaignDialogTrigger({
  sourceParam,
  sourceValidation,
  param,
  discountCode,
  cookie,
  cookieContains,
  logicOperator = "OR",
  dialogSelector,
  campaignTitleTemplate = "",
  codeTemplate = "",
  contentOverride,
}) {
  let shouldOpen = false;
  let paramValue = null;
  let discountCodeValue = null;

  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);

  // if urlparams contains param named shareableLink return immediately
  if (urlParams.has("shareableLink")) {
    return;
  }

  if (sourceParam && sourceValidation) {
    // Only proceed if the URL param exists and matches the expected value
    if (!urlParams.has(sourceParam)) {
      return;
    }
    if (urlParams.get(sourceParam) !== sourceValidation) {
      return;
    }
  }

  if (param && urlParams.has(param)) {
    paramValue = urlParams.get(param);
    paramValue = paramValue.trim().replaceAll("_", " ").toLocaleUpperCase();
  }
  if (discountCode && urlParams.has(discountCode)) {
    discountCodeValue = urlParams.get(discountCode);
  }

  if (cookie) {
    const cookieValue = getCookie(cookie);
    if (cookieValue === null) return;
    if (cookieContains) {
      if (cookieValue.includes(cookieContains)) {
        discountCodeValue = cookieValue;
      }
    } else {
      discountCodeValue = cookieValue;
    }
  }

  if (logicOperator === "AND") {
    // if paramValue and discountCodeValue exist
    shouldOpen = paramValue && discountCodeValue;
  } else {
    shouldOpen = paramValue || discountCodeValue;
  }

  // Cookie check and redirect logic (before showing modal)
  if (shouldOpen && discountCodeValue) {
    // Only run for cases where discountCode is set
    if (discountCode) {
      const cookieValue = getCookie("discount_code");
      if (cookieValue !== discountCodeValue) {
        // Build redirect URL
        const currentPathWithParams = window.location.pathname + window.location.search;
        const redirectUrl = `/discount/${discountCodeValue}?redirect=${currentPathWithParams.replace("?", "?&")}`;
        window.location.replace(redirectUrl);
        return;
      }
    }
  }

  if (shouldOpen) {
    // console.log('[campaignDialogTrigger] shouldOpen:', dialogSelector);
    waitForEl(dialogSelector).then((dialog) => {
    //   console.log('[campaignDialogTrigger] waitForEl resolved:', dialogSelector, dialog);
      if (!dialog) {
        console.error("[campaignDialogTrigger] Dialog element not found for selector:", dialogSelector);
        return;
      }
      // Set title and content if elements exist
      const titleEl = dialog.querySelector("[data-campaign-modal-campaign]");
      const codeEl = dialog.querySelector("[data-campaign-modal-code]");
      const contentEl = dialog.querySelector("[data-campaign-modal-content]");
      if (titleEl) {
        titleEl.textContent = campaignTitleTemplate.replace(
          "${param}",
          paramValue
        );
      }
      if (codeEl && codeTemplate) {
        codeEl.textContent = codeTemplate.replace(
          "${discountCode}",
          discountCodeValue
        );
      }
      if (contentEl && contentOverride) {
        contentEl.innerHTML = contentOverride;
      }
      // Always attach close/copy/ESC logic
      const closeBtn = dialog.querySelector('[data-close-dialog]');
      if (closeBtn) {
        // console.log('[campaignDialogTrigger] Found close button:', closeBtn);
        closeBtn.onclick = function() {
        //   console.log('[campaignDialogTrigger] Close button clicked');
          dialog.close();
        };
      } else {
        console.warn('[campaignDialogTrigger] Close button not found');
      }
      const copyButton = dialog.querySelector("#copy-campaign-code-button");
      if (copyButton) {
        // console.log('[campaignDialogTrigger] Found copy button:', copyButton);
        copyButton.onclick = () => {
        //   console.log('[campaignDialogTrigger] Copy button clicked');
          copyElementTextToClipboard("[data-campaign-modal-code]");
          // Optionally close dialog after copy:
          dialog.close();
        };
      } else {
        console.warn('[campaignDialogTrigger] Copy button not found');
      }
      dialog.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
        //   console.log('[campaignDialogTrigger] ESC pressed, closing dialog');
          dialog.close();
        }
      });
      // Show dialog
      if (typeof dialog.showModal === "function") {
        // console.log('[campaignDialogTrigger] Opening dialog with showModal:', dialogSelector);
        dialog.showModal();
      } else if (typeof dialog.show === "function") {
        // console.log('[campaignDialogTrigger] Opening dialog with show:', dialogSelector);
        dialog.show();
      } else {
        console.error('[campaignDialogTrigger] dialog.show/showModal not found. typeof dialog:', typeof dialog, 'Keys:', Object.keys(dialog));
        dialog.classList.add("active");
      }
    }).catch((err) => {
      console.error('[campaignDialogTrigger] waitForEl error:', err);
    });
  }
}

// IDME
campaignDialogTrigger({
  param: "popup",
  cookie: "discount_code",
  cookieContains: "IDME",
  logicOperator: "AND",
  dialogSelector: "#campaign-modal",
  campaignTitleTemplate: "Heroes Discount",
  codeTemplate: "${discountCode}",
});

// YoutubeInfluencers
campaignDialogTrigger({
  sourceParam: "utm_source",
  sourceValidation: "youtube",
  param: "utm_campaign",
  discountCode: "code",
  logicOperator: "AND",
  dialogSelector: "#campaign-modal",
  campaignTitleTemplate: "${param}",
  codeTemplate: "${discountCode}",
});

// YoutubeInfluencers2
campaignDialogTrigger({
  param: "utm_campaign",
  sourceParam: "utm_source",
  sourceValidation: "youtube",
  cookie: "discount_code",
  cookieContains: "02-PD_YT",
  logicOperator: "AND",
  dialogSelector: "#campaign-modal",
  campaignTitleTemplate: "${param}",
  codeTemplate: "${discountCode}",
});

// Ambassadors
campaignDialogTrigger({
  sourceParam: "utm_source",
  sourceValidation: "ambassador",
  param: "utm_campaign",
  dialogSelector: "#campaign-modal",
  campaignTitleTemplate: "${param}",
  contentOverride: window.ambassadorsOffer,
});
